import { useState, useEffect, createContext, useContext } from "react";
import {
  IconButton,
  Button,
  Box,
  Popper,
  Paper,
  Typography,
  ClickAwayListener,
  Divider,
  TextField,
  InputAdornment,
  Checkbox,
  Avatar,
  Radio,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import "../../styles/CustomFilter.css";
import DefaultSortIcon from "../../assets/common/Default-sort-icon.svg";
import AscSortIcon from "../../assets/common/Asc-sort-icon.svg";
import DescSortIcon from "../../assets/common/Desc-sort-icon.svg";
import { capitalizeFirstLetter, getAvatarColor, getAvatarInitials, NumberFilterValue } from "../../utils/utils";
import EditColumnIcon from "../../assets/action-items/edit-column-icon.svg";
import EnterIcon from "../../assets/common/enter-arrow-icon.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import React from "react";
import ColumnFilterIcon from "../../assets/action-items/column-filter-icon.svg";
import { useNavigate } from "react-router-dom";
import SearchFilterIcon from "../../assets/common/search-filter-icon.svg";
import useGlobalContext from "../../context/useGlobalContext";
import { DateRangeValue } from "../../utils/types";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { normalizeToFullDay } from "../../utils/utils";

interface Option {
  label: string;
  value: string;
  avatar?: string; // Optional avatar URL
  icon?: string;
  color?: string;
  type?: "assignee" | "coach" | "account" | "other" | "tags" | "subscribers" | "delay";
  defaultValue?: boolean;
  isPinned?: boolean;
  pinned?: boolean;
  email?: string;
  entityType?: "user" | "space";
  labelColour?: string;
}

/** Opt-in: only pages that wrap with this provider get exclusive column-filter open behavior. */
export const ExclusiveColumnFilterContext = createContext<{
  activeKey: string | null;
  setActiveKey: (key: string | null) => void;
} | null>(null);

interface ColumnFilterProps {
  currentValue: string | Record<string, boolean> | string[] | NumberFilterValue | DateRangeValue | undefined;
  options: Option[];
  onApply: (value: string | Record<string, boolean> | string[] | NumberFilterValue | DateRangeValue | undefined) => void;
  type: "single" | "multi" | "toggle" | "number" | "date";
  showSections?: {
    sort?: boolean;
    search?: boolean;
    options?: boolean;
    apply?: boolean;
  };
  columnKey?: string; // Identify the column (e.g., "dueDate")
  sortDirection?: "asc" | "desc" | null; // Current sort direction
  onSortChange?: (columnKey: string, direction: "asc" | "desc") => void;
  placeHolder?: string;
  isColumnFilter?: boolean;
  anchorElProp?: HTMLElement | null;
  popperRef?: React.RefObject<HTMLDivElement>;
  onOpen?: () => any;
  onClose?: () => void;
  isActive?: boolean;
  parent_page?: string;
  enableSearchMode?: boolean;
  onReorder?: (newOrderKeys: string[]) => void;
  enableColumnReordering?: boolean;
  onSearchChange?: (query: string) => void;
  isLoading?: boolean | ((colKey: string) => boolean);
  hideUntilSearchThreshold?: number;
  numberPlaceholder?: string;
}

const ColumnFilter: React.FC<ColumnFilterProps> = ({
  currentValue,
  onApply,
  options,
  type,
  showSections,
  columnKey,
  sortDirection: propSortDirection,
  onSortChange,
  placeHolder,
  isColumnFilter,
  anchorElProp,
  popperRef,
  onOpen,
  onClose,
  isActive,
  parent_page,
  enableSearchMode = false,
  onReorder,
  enableColumnReordering = false,
  onSearchChange,
  isLoading = false,
  hideUntilSearchThreshold,
  numberPlaceholder = "Insert MRR",
}: ColumnFilterProps) => {
  const isColLoading = typeof isLoading === "function" ? isLoading(columnKey || "") : Boolean(isLoading);
  const exclusiveFilter = useContext(ExclusiveColumnFilterContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<string | string[]>(
    type === "multi" ? (Array.isArray(currentValue) ? currentValue : []) : (currentValue as string) || ""
  );
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>(() => {
    const states: Record<string, boolean> = {};
    options.forEach((opt) => {
      // If defaultValue is defined, use it; otherwise, use currentValue or default to true
      states[opt.value] =
      opt.defaultValue !== undefined
      ? opt.defaultValue
      : (currentValue as Record<string, boolean>)?.[opt.value] ?? true;
    });
    return states;
  });
  const [initialSelected, setInitialSelected] = useState(selected);
  const [initialToggleStates, setInitialToggleStates] = useState<Record<string, boolean>>(toggleStates);
  const toSearchString = (value: any): string =>
    typeof value === "string"
      ? value
      : Array.isArray(value)
      ? String(value[0] ?? "")
      : "";

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [draggedKey, setDraggedKey] = useState<string | null>(null);

  const toggleOptionsContainerRef = React.useRef<HTMLDivElement | null>(null);

  const isOptionPinned = (opt?: Option) => {
    if (!opt) return false;
    return Boolean(opt.isPinned || opt.pinned);
  };

  const handleDragStart = (e: React.DragEvent, key: string) => {
    const opt = options.find((o) => o.value === key);
    if (isOptionPinned(opt)) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
    setDraggedKey(key);
  };

  const handleDragOver = (e: React.DragEvent, targetKey?: string) => {
    e.preventDefault();

    if (enableColumnReordering && toggleOptionsContainerRef.current) {
      const container = toggleOptionsContainerRef.current;
      const rect = container.getBoundingClientRect();
      const threshold = 40;
      const scrollSpeed = 12;

      if (e.clientY - rect.top < threshold) {
        container.scrollTop -= scrollSpeed;
      } else if (rect.bottom - e.clientY < threshold) {
        container.scrollTop += scrollSpeed;
      }
    }

    if (targetKey) {
      const targetOpt = options.find((o) => o.value === targetKey);
      if (isOptionPinned(targetOpt)) {
        e.dataTransfer.dropEffect = "none";
        return;
      }
    }
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetKey: string) => {
    e.preventDefault();
    const sourceKey = e.dataTransfer.getData("text/plain") || draggedKey;
    if (!sourceKey || sourceKey === targetKey) return;

    const sourceOpt = options.find((o) => o.value === sourceKey);
    const targetOpt = options.find((o) => o.value === targetKey);

    if (isOptionPinned(sourceOpt) || isOptionPinned(targetOpt)) {
      return;
    }

    const sourceIndex = options.findIndex((o) => o.value === sourceKey);
    const targetIndex = options.findIndex((o) => o.value === targetKey);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const currentOptions = [...options];
    const [movedItem] = currentOptions.splice(sourceIndex, 1);
    currentOptions.splice(targetIndex, 0, movedItem);

    const newOrderKeys = currentOptions.map((opt) => opt.value);
    setDraggedKey(null);

    if (onReorder) {
      onReorder(newOrderKeys);
    }
  };
  const trimmedSearchQuery = searchQuery?.trim();
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(propSortDirection || null);
  const isDescriptionColumn = enableSearchMode === true;
  const appliedValue = toSearchString(currentValue);
  const hasSearchValueChanged =
  isDescriptionColumn &&
  (searchQuery === "" || searchQuery !== appliedValue);
  const navigate = useNavigate();
  const { featureMap } = useGlobalContext();
  const userPreferences = featureMap?.user_preferences === true;

  const [numberDraft, setNumberDraft] = useState<NumberFilterValue | undefined>(
    type === "number" && currentValue && typeof currentValue === "object"
      ? (currentValue as NumberFilterValue)
      : undefined
  );
  const [dateDraft, setDateDraft] = useState<DateRangeValue | undefined>(
    type === "date" && currentValue && typeof currentValue === "object" && !Array.isArray(currentValue)
      ? (currentValue as DateRangeValue)
      : undefined
  );
  const showOptions = showSections?.options;

  const [operatorOpen, setOperatorOpen] = useState(false);

  const accountOptions: Option[] =
    columnKey === "account" && parent_page !== "meetings-page" && parent_page !== "opportunities-page"
      ? [
          {
            value: "no_account",
            label: "No Account",
            type: "account",
            avatar: "",
            icon: "",
            color: "",
            email: "",
          },
          ...options,
        ]
      : options;

  // Then use accountOptions instead of options for anything inside the component

  const shouldShow = (key: keyof NonNullable<typeof showSections>) => {
    if (!showSections) return true;
  
    if (isDescriptionColumn) {
      return showSections[key] !== false;
    }
  
    return showSections[key] !== false;
  };  

  const isFirstSearchRender = React.useRef(true);
  const onSearchChangeRef = React.useRef(onSearchChange);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    const delay = onSearchChangeRef.current ? 1000 : 250;
    const id = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (onSearchChangeRef.current) {
        onSearchChangeRef.current(searchQuery);
      }
    }, delay);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    if (type === "toggle") {
      const newStates: Record<string, boolean> = {};
      options.forEach((opt) => {
        // Lock to defaultValue if defined, otherwise use currentValue or true
        newStates[opt.value] =
          opt.defaultValue !== undefined
            ? opt.defaultValue
            : (currentValue as Record<string, boolean>)?.[opt.value] ?? true;
      });
      setToggleStates(newStates);
    }
  }, [currentValue, type, options]);

  useEffect(() => {
    if (type === "multi") {
      const newSelected = Array.isArray(currentValue) ? currentValue : [];
      setSelected(newSelected);
      setInitialSelected(newSelected);
    } else if (type === "toggle") {
      const newStates: Record<string, boolean> = {};
      options.forEach((opt) => {
        newStates[opt.value] =
          opt.defaultValue !== undefined
            ? opt.defaultValue
            : (currentValue as Record<string, boolean>)?.[opt.value] ?? true;
      });
      setToggleStates(newStates);
      setInitialToggleStates(newStates);
    } else {
      setSelected(currentValue as string || "");
      setInitialSelected(currentValue as string || "");
    }
  }, [currentValue, type, options]);
  

  useEffect(() => {
    setSortDirection(propSortDirection || null); // Sync with parent
  }, [propSortDirection]);

  // Sync the search text with currentValue when used as a search-only filter (description)
  useEffect(() => {
    if (isDescriptionColumn && showOptions === false) {
      setSearchQuery(toSearchString(currentValue));
    }
  }, [currentValue, isDescriptionColumn, showOptions]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    if (columnKey && exclusiveFilter) {
      exclusiveFilter.setActiveKey(columnKey);
    }
    onOpen?.();
  };

  // Only when ExclusiveColumnFilterContext is provided (MM NA report) — other pages unaffected
  useEffect(() => {
    if (!exclusiveFilter) return;
    if (exclusiveFilter.activeKey !== columnKey && anchorEl) {
      setAnchorEl(null);
    }
  }, [exclusiveFilter, exclusiveFilter?.activeKey, columnKey, anchorEl]);

  const displayOptions = React.useMemo(() => {
    let updatedOptions = accountOptions;

    if ((columnKey === "tags" || columnKey === "account") && type === "multi") {
      updatedOptions = [...accountOptions].sort((a, b) => {
        const aSelected = (selected as string[]).includes(a.value);
        const bSelected = (selected as string[]).includes(b.value);

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        return a.label.localeCompare(b.label);
      });
    }

    return updatedOptions;
  }, [accountOptions, selected, columnKey, type]);

  const handleClose = () => {
    if (isColumnFilter) return;
    setAnchorEl(null);
    if (columnKey && exclusiveFilter?.activeKey === columnKey) {
      exclusiveFilter.setActiveKey(null);
    }
    onClose?.();
    setSortDirection(propSortDirection || null);
    if(columnKey !== "description"){
      setSearchQuery("");
    }
    if (type === "multi") {
      setSelected(initialSelected as string[]);
    } else if (type === "toggle") {
      setToggleStates(initialToggleStates);
    } else if (type === "date") {
      setDateDraft(
        currentValue && typeof currentValue === "object" && !Array.isArray(currentValue)
          ? (currentValue as DateRangeValue)
          : undefined
      );
    } else {
      setSelected(initialSelected as string);
    }
  };

  const handleSelectSingle = (value: string) => {
    setSelected(value);
    if (columnKey === "rating" || columnKey === "relationshipType" || columnKey === "trajectory") {
      onApply(value);
      handleClose();
    }
};

  const handleSelectMulti = (value: string) => {
    setSelected((prev) => {
      const prevArray = prev as string[];
      if (prevArray.includes(value)) {
        return prevArray.filter((v) => v !== value);
      }
      return [...prevArray, value];
    });
  };

  const handleApply = () => {
    if (type === "date") {
      if (!dateDraft?.startDate || !dateDraft?.endDate) {
        return;
      }

      onApply(dateDraft);
      handleClose();
      return;
    }

    onApply(selected);
    handleClose();
  };

  const handleClear = () => {
    if (type === "date") {
      setDateDraft(undefined);
      onApply(undefined);
      handleClose();
      return;
    }

    const clearedValue = type === "multi" ? [] : "";
    setSelected(clearedValue);
    onApply(clearedValue);
    handleClose();
  };

  const popperPlacement =
  isDescriptionColumn ? "bottom-start" :
  type === "toggle" ? "bottom-end" :
  type === "date"
    ? columnKey === "end_date"
      ? "bottom-end"
      : "bottom-start"
    : "bottom";

  const handleSearchSubmit = () => {
    if (trimmedSearchQuery === "") return;
    onApply(trimmedSearchQuery);
    handleClose();
  };
  
  const popperAnchorEl = isColumnFilter ? anchorElProp : anchorEl;
  const open = Boolean(popperAnchorEl);
  const normalizeKey = (key?: string) => (key || "").replace(/[\s_]+/g, "").toLowerCase();
  const hideList = [
    "assignee",
    "coach",
    "sponsor",
    "account",
    "subscribers",
    "aeTeam",
    "stage",
  ];
  useEffect(() => {
    if (type === "number" && open) {
      setNumberDraft(
        currentValue && typeof currentValue === "object"
          ? (currentValue as NumberFilterValue)
          : undefined
      );
      setOperatorOpen(false);
    }
  }, [open, currentValue, type]);

  useEffect(() => {
    if (type === "date" && currentValue && typeof currentValue === "object" && !Array.isArray(currentValue)) {
      setDateDraft(currentValue as DateRangeValue);
    }
    if (type === "date" && (!currentValue || typeof currentValue === "string")) {
      setDateDraft(undefined);
    }
  }, [currentValue, type]);

  const filteredOptions = React.useMemo(() => {
    const activeSearch = typeof searchQuery === "string" ? searchQuery.trim().toLowerCase() : "";

    if (onSearchChange) {
      if (!activeSearch) {
        return displayOptions.filter((option) => {
          const isSelected =
            type === "multi"
              ? (selected as string[]).includes(option.value)
              : selected === option.value;
          return isSelected;
        }).slice(0, 50);
      }
      return displayOptions.slice(0, 50);
    }

    const normKey = normalizeKey(columnKey);
    const useIncludes = normKey === "aeteam" || normKey === "stage";

    const results = displayOptions.filter((option) => {
      const isSelected =
        type === "multi"
          ? (selected as string[]).includes(option.value)
          : selected === option.value;

      if (isSelected) return true;

      if (
        (hideUntilSearchThreshold !== undefined && options.length <= hideUntilSearchThreshold) ||
        (normKey === "account" && options.length <= 5)
      ) {
        return true;
      }

      if (activeSearch.length > 0) {
        const label = option.label ?? "";
        return useIncludes
          ? label.toLowerCase().includes(activeSearch)
          : label.toLowerCase().startsWith(activeSearch);
      }

      const isInHideList =
        (hideUntilSearchThreshold !== undefined && options.length > hideUntilSearchThreshold) ||
        hideList.includes(option.type || "") ||
        hideList.includes(columnKey || "");

      if (isInHideList) return false;

      return true;
    });

    return results.slice(0, 50);
  }, [
    displayOptions,
    selected,
    searchQuery,
    debouncedSearch,
    type,
    columnKey,
    options.length,
    onSearchChange,
  ]);

  
  const hasSelection = type === "multi" 
    ? (selected as string[]).length > 0 
    : type === "date"
    ? Boolean(dateDraft?.startDate || dateDraft?.endDate)
    : selected !== "";

    const handleSortToggle = () => {
      setSortDirection((prev) => {
        const newDirection =
        prev === null ? "desc" : prev === "desc" ? "asc" : "desc"; // Cycle between asc and desc only
        if (columnKey && onSortChange) {
          onSortChange(columnKey, newDirection); // Notify parent
        }
        return newDirection;
      });
      handleClose();
    };

    const handleToggleChange = (key: string) => {
      // Only allow toggling if defaultValue is undefined
      const option = options.find((opt) => opt.value === key);
      if (option?.defaultValue !== undefined) return;
  
      setToggleStates((prev) => {
        const updated = { ...prev, [key]: !prev[key] };
        const final = { ...updated };
        options.forEach((opt) => {
          if (opt.defaultValue !== undefined) {
            final[opt.value] = opt.defaultValue;
          }
        });
    
        onApply(final);
        return updated;
      });
    };
    
    const hasChanges = (() => {
      if (type === "multi") {
    const initial = initialSelected as string[];
    const curr = selected as string[];
    return initial.length !== curr.length || !initial.every(v => curr.includes(v));
    } else if (type === "date") {
      const initial = currentValue && typeof currentValue === "object" && !Array.isArray(currentValue)
        ? (currentValue as DateRangeValue)
        : undefined;

      return Boolean(dateDraft?.startDate || dateDraft?.endDate) &&
        (
          dateDraft?.startDate !== initial?.startDate ||
          dateDraft?.endDate !== initial?.endDate
        );
    } else if (type === "toggle") {
      return options.some(opt => toggleStates[opt.value] !== initialToggleStates[opt.value]);
    }
      return selected !== initialSelected;
    })();

    const shouldHideOptions = (() => {
      const isThresholdActive = hideUntilSearchThreshold !== undefined && options.length > hideUntilSearchThreshold;
      const isInHideList = isThresholdActive || hideList.includes(options[0]?.type || "") || hideList.includes(columnKey || "");
      if (
        (hideUntilSearchThreshold !== undefined && options.length <= hideUntilSearchThreshold) ||
        (columnKey === "account" && options.length <= 5)
      ) {
        return false;
      }
      return isInHideList && 
      searchQuery.length === 0 && 
      filteredOptions.length === 0;
    })();

  const isSubscriberGrouping = columnKey === "subscribers";
  const groupedSubscriberOptions = {
    users:
      isSubscriberGrouping
        ? filteredOptions.filter(opt => opt.entityType === "user")
        : [],
    spaces:
      isSubscriberGrouping
        ? filteredOptions.filter(opt => opt.entityType === "space")
        : [],
  };
  const renderMultiOption = (option: Option) => {
  const isSelected = (selected as string[]).includes(option.value);

  return (
    <Box
      key={option.value}
      onClick={() => handleSelectMulti(option.value)}
      className={`cf-multi-option ${isSelected ? "selected" : ""}`}
    >
      <Box className="cf-multi-option-content">
        {(option.type === "assignee" ||
          option.type === "coach" ||
          option.type === "subscribers"||
          option.entityType === "space") && (
          <Avatar
            variant={option.entityType === "space" ? "square" : undefined}
            src={option.avatar || ""}
            alt={option.label || ""}
            className={
              option.entityType === "space"
                ? "cf-space-avatar"
                : "cf-avatar"
            }
            sx={{
              width: 28,
              height: 28,
              bgcolor: !option.avatar
                ? getAvatarColor(
                    option.email || option.label || option.value
                  )
                : undefined,
              fontSize: 14,
            }}
          >
            {!option.avatar &&
              getAvatarInitials(
                option.email || option.label || option.value
              )}
          </Avatar>
        )}

        <Typography className="cf-multi-option-label">
          {capitalizeFirstLetter(option.label)}
        </Typography>
      </Box>

      <Checkbox checked={isSelected} className="cf-checkbox" />
    </Box>
  );
};
const handleClick = () => {
  navigate("/settings/user-settings?tab=CONTROLS");
};

  return (
    <>
        {!isColumnFilter && (
          type === "toggle" ? (
            <Tooltip title="Edit columns" arrow placement="top">
              <div className = {`add-column-box ${anchorEl !== null? "active" : ""}`} onClick={handleOpen}>
                    <img
                      className={`custom-filter-icon-edit ${hasSelection ? "active" : ""} toggle-icon`}
                      src={EditColumnIcon}
                      alt="Filter"
                    />
              </div>
            </Tooltip>
          ) : (
            <Tooltip title="" arrow placement="top">
              <IconButton
                size="small"
                onClick={handleOpen}
                disableRipple
                // sx={{ "&:hover": { backgroundColor: "transparent !important" } }}
                className={`cf-icon-button ${isActive ? "active" : ""}`}
              >
                <img
                  className={`custom-filter-icon ${hasSelection ? "active" : ""}`}
                  src={SearchFilterIcon}
                  alt="Filter"
                />
              </IconButton>
            </Tooltip>
          )
        )}

      <Popper open={open} ref={popperRef} anchorEl={popperAnchorEl}  placement={popperPlacement}
      sx={{
        borderRadius: "4px"
      }}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: type === "toggle" ? [16, 10] : [0, 2],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8, // 8px from viewport edges
          },
        },
      ]}
       className="cf-popper">
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
        {type === "number" ? (
            <Paper className="cf-paper-mrr">
              {shouldShow("sort") && (
                <Box
                  className={`cf-sort-container ${sortDirection ? "active" : ""}`}
                  onClick={handleSortToggle}
                >
                  <img
                    src={
                      sortDirection === null
                        ? DefaultSortIcon
                        : sortDirection === "asc"
                        ? AscSortIcon
                        : DescSortIcon
                    }
                    alt="Sort Icon"
                    className="cf-sort-icon"
                  />
                  <Typography sx={{ cursor: "pointer" }}>
                    {sortDirection === null
                      ? "Sort"
                      : sortDirection === "asc"
                      ? "Asc"
                      : "Desc"}
                  </Typography>
                </Box>
              )}
              <Box className="mrr-operator-wrapper">
                <Box
                  className="mrr-operator-trigger"
                  onClick={() => setOperatorOpen((p) => !p)}
                >
                  <Typography>
                    {numberDraft?.operator === "gt"
                      ? "Greater than"
                      : numberDraft?.operator === "lt"
                      ? "Less than"
                      : "Select option"}
                  </Typography>
                  <KeyboardArrowDownIcon fontSize="small" />
                </Box>

                {operatorOpen && (
                  <Box className="mrr-operator-dropdown">
                    <Box
                      className="mrr-operator-option"
                      onClick={() => {
                        setNumberDraft({
                          operator: "gt",
                          value: numberDraft?.value ?? 0,
                        });
                        setOperatorOpen(false);
                      }}
                    >
                      Greater than
                    </Box>

                    <Box
                      className="mrr-operator-option"
                      onClick={() => {
                        setNumberDraft({
                          operator: "lt",
                          value: numberDraft?.value ?? 0,
                        });
                        setOperatorOpen(false);
                      }}
                    >
                      Less than
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Input */}
              {numberDraft?.operator && (
                <Box className="mrr-input">
                  <span>$</span>
                  <input
                    type="number"
                    placeholder={numberPlaceholder}
                    value={numberDraft.value || ""}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (val >= 0) {
                        setNumberDraft({
                          operator: numberDraft.operator,
                          value: val,
                        });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Box>
              )}

              {/* Buttons */}
              <Box className="cf-buttons">
                <Button
                  className="cf-clear-btn"
                  onClick={() => {
                    setNumberDraft(undefined);
                    onApply(undefined);
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="cf-apply-btn"
                  disabled={!numberDraft?.value}
                  onClick={() => {
                    onApply(numberDraft);
                    handleClose();
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Paper>
          ) : type === "date" ? (
            <Paper className="cf-paper">
              {shouldShow("sort") && (
                <Box
                  className={`cf-sort-container ${sortDirection ? "active" : ""}`}
                  onClick={handleSortToggle}
                >
                  <img
                    src={
                      sortDirection === null
                        ? DefaultSortIcon
                        : sortDirection === "asc"
                        ? AscSortIcon
                        : DescSortIcon
                    }
                    alt="Sort Icon"
                    className="cf-sort-icon"
                  />
                  <Typography sx={{ cursor: "pointer" }}>
                    {sortDirection === null
                      ? "Sort"
                      : sortDirection === "asc"
                      ? "Asc"
                      : "Desc"}
                  </Typography>
                </Box>
              )}
              <Box className="cf-date-column-filter" sx={{ p: 1, width: 340 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                    <DatePicker
                      label="Start Date"
                      value={dateDraft?.startDate ? new Date(dateDraft.startDate) : null}
                      onChange={(newValue) => {
                        setDateDraft((prev) => {
                          if (!newValue) {
                            return prev?.endDate
                              ? { preset: "custom", startDate: "", endDate: prev.endDate }
                              : undefined;
                          }

                          const nextStart = new Date(newValue);
                          const prevEnd = prev?.endDate ? new Date(prev.endDate) : null;
                          const nextEnd =
                            prevEnd && nextStart.getTime() > prevEnd.getTime()
                              ? nextStart
                              : prevEnd || nextStart;

                          return {
                            preset: "custom",
                            startDate: nextStart.toISOString(),
                            endDate: nextEnd.toISOString(),
                          };
                        });
                      }}
                      format="MMM dd, yyyy"
                      views={["year", "month", "day"]}
                      slotProps={{
                        textField: { fullWidth: true, variant: "outlined", placeholder: "MMM/DD/YYYY", },
                        popper: {
                          disablePortal: true,
                        },
                      }}
                    />
                    <DatePicker
                      label="End Date"
                      value={dateDraft?.endDate ? new Date(dateDraft.endDate) : null}
                      minDate={dateDraft?.startDate ? new Date(dateDraft.startDate) : undefined}
                      onChange={(newValue) => {
                        setDateDraft((prev) => {
                          if (!newValue) {
                            return prev?.startDate
                              ? { preset: "custom", startDate: prev.startDate, endDate: "" }
                              : undefined;
                          }

                          const nextEnd = new Date(newValue);
                          const prevStart = prev?.startDate ? new Date(prev.startDate) : null;
                          const nextStart =
                            prevStart && nextEnd.getTime() < prevStart.getTime()
                              ? nextEnd
                              : prevStart || nextEnd;

                          return {
                            preset: "custom",
                            startDate: nextStart.toISOString(),
                            endDate: nextEnd.toISOString(),
                          };
                        });
                      }}
                      format="MMM dd, yyyy"
                      views={["year", "month", "day"]}
                      slotProps={{
                        textField: { fullWidth: true, variant: "outlined", placeholder: "MMM/DD/YYYY",},
                        popper: {
                          disablePortal: true,
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
                <Box className="cf-buttons" sx={{ justifyContent: "space-between", mt: 1.5, backgroundColor: "#fffff" }}>
                  <Button
                    className="cf-apply-btn"
                    disabled={
                      !dateDraft?.startDate ||
                      !dateDraft?.endDate ||
                      !hasChanges
                    }
                    onClick={() => {
                      if (!dateDraft?.startDate || !dateDraft?.endDate) {
                        return;
                      }

                      const normalized = normalizeToFullDay(
                        new Date(dateDraft.startDate),
                        new Date(dateDraft.endDate)
                      );

                      onApply({
                        preset: "custom",
                        startDate: normalized.startDate.toISOString(),
                        endDate: normalized.endDate.toISOString(),
                      });
                      handleClose();
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    className="cf-clear-btn"
                    onClick={() => {
                      setDateDraft(undefined);
                      onApply(undefined);
                      handleClose();
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </Paper>
          ) : type === "single" ? (
            <Paper className="cf-paper">
              {shouldShow("sort") && (
                <Box
                  className={`cf-sort-container ${sortDirection ? "active" : ""}`}
                  onClick={handleSortToggle}
                >
                  <img
                    src={
                      sortDirection === null
                        ? DefaultSortIcon
                        : sortDirection === "asc"
                        ? AscSortIcon
                        : DescSortIcon
                    }
                    alt="Sort Icon"
                    className="cf-sort-icon"
                  />
                  <Typography sx={{ cursor: "pointer" }}>
                    {sortDirection === null
                      ? "Sort"
                      : sortDirection === "asc"
                      ? "Asc"
                      : "Desc"}
                  </Typography>
                </Box>
              )}
              {shouldShow("options") && (
                <Box className="cf-options">
                  {options.map((option) => {
                    const isSelected = selected === option.value;
                    return (
                      <Box
                        key={option.value}
                        onClick={() => handleSelectSingle(option.value)}
                        className={`cf-option ${isSelected ? "selected" : ""}`}
                        sx={{ position: "relative" }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {option.labelColour && (
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "4px",
                                bgcolor: option.labelColour,
                              }}
                            />
                          )}
                          <Typography className="cf-option-label">
                            {option.label}
                          </Typography>
                        </Box>
                        <Box className={`cf-circle ${isSelected ? "selected-circle" : ""}`} />
                      </Box>
                    );
                  })}
                </Box>
              )}

              {shouldShow("options") && shouldShow("apply") && <Divider />}

              {shouldShow("apply") &&
                columnKey !== "rating" &&
                columnKey !== "attentionNeeded" && 
                columnKey !== "trajectory" &&(
                  <Box className="cf-buttons" sx={{justifyContent: columnKey === "rating" || columnKey === "attentionNeeded" || columnKey === "trajectory" ? "center" : "space-between"}}>
                    <Button className="cf-clear-btn" onClick={handleClear}>
                      Clear
                    </Button>
                    <Button className="cf-apply-btn" onClick={handleApply}>
                      Apply
                    </Button>
                  </Box>
                )}
            </Paper>
          ) : type === "multi" ? (
            <Paper className="cf-paper-multi">
              {shouldShow("sort") && (
              <Box className={`cf-sort-container ${sortDirection ? "active" : ""}`} onClick={handleSortToggle}>
                <img
                  src={
                    sortDirection === null
                    ? DefaultSortIcon
                    : sortDirection === "asc"
                    ? AscSortIcon
                    : DescSortIcon
                  }
                  alt="Sort Icon"
                  className="cf-sort-icon"
                  />
                  <Typography sx={{cursor: "pointer"}}>{sortDirection === null ? "Sort" : sortDirection === "asc" ? "Asc" : "Desc"}</Typography>
              </Box>
              )}
              {shouldShow("search") && (
              <Box className="cf-search-container">
                <TextField
                  fullWidth
                  placeholder= {placeHolder || "Search by name"}
                  value={searchQuery}
                  autoFocus
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSearchQuery(newValue);
                  }}
                  onKeyDown={(e) => {
                    if (
                      isDescriptionColumn &&
                      e.key === "Enter" &&
                      trimmedSearchQuery !== ""
                    ) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSearchSubmit();
                    }
                  }}
                  variant="outlined"
                  size="small"
                  className="cf-search-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className="cf-search-icon" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {isDescriptionColumn ? (
                          hasSearchValueChanged ? (
                            // SHOW SEARCH BUTTON
                            <button
                            className={`inline-btn inline-btn-primary ${
                              trimmedSearchQuery !== "" ? "" : "disabled"
                            }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSearchSubmit();
                              }}
                            >
                              <span className="inline-btn-text">Search</span>
                              <img src={EnterIcon} alt="enter" className="enter-icon" />
                            </button>
                          ) : (
                            //  SHOW CLEAR ICON
                            searchQuery && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchQuery("");
                                  onApply("");
                                  handleClose();
                                }}
                              >
                                ✕
                              </IconButton>
                            )
                          )
                        ) : (
                          // existing behavior for other columns
                          searchQuery && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery(""); 
                              }}
                            >
                              ✕
                            </IconButton>
                          )
                        )}
                      </InputAdornment>
                    ),                                   
                  }}
                />
              </Box>
              )}
              {shouldShow("options") && !shouldHideOptions && (
                <Box className="cf-multi-options">
                  {isColLoading && filteredOptions.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 2 }}>
                      <CircularProgress size={24} sx={{ color: "#6036d6" }} />
                    </Box>
                  ) : (
                    <>
                      {isColLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 1 }}>
                          <CircularProgress size={18} sx={{ color: "#6036d6" }} />
                        </Box>
                      )}
                      {filteredOptions.length === 0 ? (
                        <Box sx={{ p: 2, textAlign: "center", color: "#6C6C78", fontSize: "13px" }}>
                          {!searchQuery.trim() && onSearchChange
                            ? columnKey === "accountName" || columnKey === "account"
                              ? "Type to search accounts"
                              : "Type to search..."
                            : "No matches found"}
                        </Box>
                      ) : isSubscriberGrouping ? (
                <>
                  {groupedSubscriberOptions?.spaces.length > 0 && (
                    <>
                      <Typography className="cf-section-header">
                        Spaces
                      </Typography>

                      {groupedSubscriberOptions.spaces.map((option) =>
                        renderMultiOption(option)
                      )}
                    </>
                  )}
                  {groupedSubscriberOptions?.users.length > 0 && (
                    <>
                      <Typography className="cf-section-header">
                        People
                      </Typography>

                      {groupedSubscriberOptions.users.map((option) =>
                        renderMultiOption(option)
                      )}
                    </>
                  )}
                </>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = (selected as string[]).includes(option.value);
                  return (
                    <Box
                      key={option.value}
                      onClick={() => handleSelectMulti(option.value)}
                      className={`cf-multi-option ${isSelected ? "selected" : ""}`}
                    >
                      <Box className="cf-multi-option-content">
                      {option.avatar ? (
                        // Case 1: Avatar (image URL)
                        <Avatar
                        src={option.avatar || ""}
                        alt={option.label || ""}
                        className="cf-avatar"
                        sx={{ width: 32, height: 32, bgcolor: !option?.avatar ? getAvatarColor(option?.email || option?.label || option?.value ) : undefined, fontSize: 14}}
                      >
                        {!option?.avatar && getAvatarInitials ( option?.email || option?.label || option?.value )}
                      </Avatar>
                      ) : option.icon ? (
                        // Case 2: Icon (SVG or PNG path)
                        <img
                          src={option.icon}
                          alt={option.label}
                          className="cf-icon-image"
                          style={{ width: 28, height: 28 }}
                        />
                      ) : option.color ? (
                        // Case 3: Color circle
                        <Radio
                          checked={true} // visually always filled
                          disableRipple
                          className= "status-radio"
                          sx={{
                            margin: "0px",
                            color: option.color,
                            "& .MuiSvgIcon-root": {
                              fill: option.color, // fill inside circle
                            },
                          }}
                        />
                      ) : option.labelColour ? (
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "4px",
                            bgcolor: option.labelColour,
                          }}
                        />
                      ) :
                        option.type === "assignee" || option.type === "coach" || option.type ==="subscribers" || option.entityType === "space" ? (
                          <Avatar
                            variant={option.entityType === "space" ? "square" : undefined}
                            src={option.avatar || ""}
                            alt={option.label || ""}
                            className={option.entityType === "space" ? "cf-space-avatar" : "cf-avatar"}
                            sx={{ width: 28, height: 28, bgcolor: !option?.avatar ? getAvatarColor(option?.email || option?.label || option?.value ) : undefined, fontSize: 14}}
                          >
                            {!option?.avatar && getAvatarInitials ( option?.email || option?.label || option?.value )}
                          </Avatar>
                          ) : 
                          option.type === "tags" ? (
                            <Box className="tags-box">
                              <Typography sx={{ fontSize: "10px" }}>
                                {option.label.toLowerCase()}
                              </Typography>
                            </Box>
                          ) :null}
                          {option.type !== "tags" && 
                            <Typography className="cf-multi-option-label">
                              {capitalizeFirstLetter(option.label)}
                            </Typography>
                          }
                      </Box>
                      <Checkbox
                        checked={isSelected}
                        className="cf-checkbox"
                      />
                    </Box>
                  );
                })
              )}
            </>
          )}
        </Box>
              )}
              {shouldShow("apply") && (
              <Box className="cf-multi-apply-container">
                <Button
                  fullWidth
                  className={`cf-multi-apply-btn ${!hasChanges ? "disabled" : ""}`}
                  onClick={handleApply}
                  variant="contained"
                  disabled={!hasChanges}
                >
                  {columnKey === "spaces" ? "Apply" : "Apply Filter"}
                </Button>
              </Box>
              )}
            </Paper>
          ) : type === "toggle" ? (
            <Paper className="cf-paper-toggle">
              <Box className="column-filter-title" >
                <h1>Fields</h1>
                {parent_page === "tasks_page" && userPreferences &&
                <Tooltip 
                  title="Setup Default View Preference"
                  placement="top"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#9595A3",
                        color: "#EFEBFB",
                        fontSize: "11px",
                        borderRadius: "6px",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#9595A3",
                      },
                    },
                    }}
                >
                  <img 
                    src={ColumnFilterIcon}
                    style={{
                      cursor: "pointer",
                      transition: "0.2s",
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.filter = "brightness(0)")}
                    onMouseOut={(e) => (e.currentTarget.style.filter = "none")}
                    onClick={handleClick}
                  />
                </Tooltip>
                }
              </Box>
              <Box
                ref={toggleOptionsContainerRef}
                className="cf-toggle-options"
                onDragOver={(e) => enableColumnReordering && handleDragOver(e)}
              >
                {options.map((option) => {
                  const isOn = toggleStates[option.value] ?? false;
                  const isNonToggleable = option.defaultValue !== undefined;
                  const isPinnedColumn = isOptionPinned(option);
                  const isDraggable = enableColumnReordering && !isPinnedColumn;
                  const isBeingDragged = isDraggable && draggedKey === option.value;
                  return (
                    <Box
                      key={option.value}
                      className="cf-toggle-option"
                      onClick={() => !isNonToggleable && handleToggleChange(option.value)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "6px 8px",
                        borderRadius: "6px",
                        transition: "background-color 0.15s ease",
                        opacity: isBeingDragged ? 0.4 : 1,
                        "&:hover": {
                          backgroundColor: "#F4F4F6",
                        },
                      }}
                    >
                      {enableColumnReordering && (
                        !isPinnedColumn ? (
                          <Box
                            className="cf-drag-handle"
                            draggable={true}
                            onDragStart={(e: React.DragEvent) => {
                              e.stopPropagation();
                              handleDragStart(e, option.value);
                            }}
                            onDragOver={(e: React.DragEvent) => {
                              e.stopPropagation();
                              handleDragOver(e, option.value);
                            }}
                            onDrop={(e: React.DragEvent) => {
                              e.stopPropagation();
                              handleDrop(e, option.value);
                            }}
                            onDragEnd={(e: React.DragEvent) => {
                              e.stopPropagation();
                              setDraggedKey(null);
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            sx={{ mr: 1 }}
                          >
                            <DragIndicatorIcon
                              sx={{
                                color: "#9595A3",
                                fontSize: "16px",
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ width: "24px", minWidth: "24px" }} />
                        )
                      )}
                      <Typography
                        className="cf-toggle-label"
                        sx={{
                          flex: 1,
                          opacity: isNonToggleable ? 0.5 : 1,
                          pointerEvents: "none",
                          userSelect: "none",
                        }}
                      >
                        {capitalizeFirstLetter(option.label)}
                        {isNonToggleable && (
                          <span style={{ fontSize: "10px", opacity: 0.5, marginLeft: "2px" }}>
                            *
                          </span>
                        )}
                      </Typography>
                      <label
                        className="cf-toggle-switch"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={() => handleToggleChange(option.value)}
                          disabled={isNonToggleable}
                        />
                        <span className="cf-slider" />
                      </label>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          ) : null}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default ColumnFilter;
